import React, { lazy, Suspense } from "react";
import { Route, Navigate } from "react-router-dom";
import { AuthHandler, ProtectedRoute } from "@/pages/MainPages";
import BrandGate from "@/components/BrandGate";

const ImmersiveLanding = lazy(() => import("@/pages/ImmersiveLanding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const Documents = lazy(() => import("@/pages/Documents"));
const SocialListening = lazy(() => import("@/pages/SocialListening"));
const Alerts = lazy(() => import("@/pages/AlertsNew"));
const History = lazy(() => import("@/pages/History"));
const Stakeholders = lazy(() => import("@/pages/Stakeholders"));
const AITools = lazy(() => import("@/pages/AITools"));
const Advanced = lazy(() => import("@/pages/Advanced"));
const Team = lazy(() => import("@/pages/Team"));
const CrisisSimulator = lazy(() => import("@/pages/CrisisSimulator"));
const MediaImpact = lazy(() => import("@/pages/MediaImpact"));
const ViralityPredictor = lazy(() => import("@/pages/ViralityPredictor"));
const AutoResponse = lazy(() => import("@/pages/AutoResponse"));
const StakeholderBehavior = lazy(() => import("@/pages/StakeholderBehavior"));
const BrandProfileSettings = lazy(() => import("@/pages/BrandProfileSettings"));
const AnalysisHistory = lazy(() => import("@/pages/AnalysisHistory"));
const Templates = lazy(() => import("@/pages/Templates"));
const StartupHub = lazy(() => import("@/pages/StartupHub"));
const SocialMedia = lazy(() => import("@/pages/SocialMedia"));
const AIAnalytics = lazy(() => import("@/pages/AIAnalytics"));
const MetaIntegration = lazy(() => import("@/pages/MetaIntegration"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Billing = lazy(() => import("@/pages/Billing"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("@/pages/SubscriptionCancel"));
const BrandSetup = lazy(() => import("@/pages/BrandSetup"));
const SocialConnections = lazy(() => import("@/pages/SocialConnections"));
const APIIntegrations = lazy(() => import("@/pages/APIIntegrations"));
const DemoMode = lazy(() => import("@/pages/DemoMode"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const SocialConnect = lazy(() => import("@/pages/SocialConnect"));
const SocialStudio = lazy(() => import("@/pages/SocialStudio"));
const SMMOfficer = lazy(() => import("@/pages/SMMOfficer"));
const ComparisonPage = lazy(() => import("@/pages/ComparisonPage"));
const MediaDirectory = lazy(() => import("@/pages/MediaDirectory"));
const PRToolkit = lazy(() => import("@/pages/PRToolkit"));
const SentimentDashboard = lazy(() => import("@/pages/SentimentDashboard"));
const WaitlistLanding = lazy(() => import("@/pages/WaitlistLanding"));
const WaitlistAdmin = lazy(() => import("@/pages/WaitlistAdmin"));
const IntelligenceDashboard = lazy(() => import("@/pages/IntelligenceDashboard"));
const PredictiveRiskDashboard = lazy(() => import("@/pages/PredictiveRiskDashboard"));
const BriefGenerator = lazy(() => import("@/pages/BriefGenerator"));
const StakeholderMap = lazy(() => import("@/pages/StakeholderIntelMap"));
const ReputationForecast = lazy(() => import("@/pages/ReputationForecast"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const CookiePolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CookiePolicy })));
const AIUsagePolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.AIUsagePolicy })));
const DataRetentionPolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.DataRetentionPolicy })));
const DataDeletionInstructions = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.DataDeletionInstructions })));
import PoliticalIntel from "@/pages/PoliticalIntel";
const TrustCenter = lazy(() => import("@/pages/TrustCenter"));

// UserProfile loaded eagerly (not lazy) to avoid chunk loading issues
import UserProfilePage from "@/pages/UserProfile";
const UserProfile = UserProfilePage;

// Simple protected route with brand gate wrapper
const PB = ({ user, loading, brandSetupComplete, children }) => (
  <ProtectedRoute user={user} loading={loading}>
    <BrandGate brandSetupComplete={brandSetupComplete}>{children}</BrandGate>
  </ProtectedRoute>
);

const P = ({ user, loading, children }) => (
  <ProtectedRoute user={user} loading={loading}>{children}</ProtectedRoute>
);

const AppRoutes = ({ user, loading, brandSetupComplete, onAuthComplete, onBrandSetupComplete }) => (
  <>
    <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <ImmersiveLanding />} />

    {/* Public routes */}
    <Route path="/pricing" element={<Pricing user={user} />} />
    <Route path="/billing" element={<ProtectedRoute user={user} loading={loading}><Billing /></ProtectedRoute>} />
    <Route path="/coming-soon" element={<Suspense fallback={<div />}><ComingSoon /></Suspense>} />
    <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <AuthPage onAuthComplete={onAuthComplete} mode="login" />} />
    <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <AuthPage onAuthComplete={onAuthComplete} mode="register" />} />
    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
    <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
    <Route path="/demo" element={<DemoMode />} />
    <Route path="/comparativa/:slug" element={<Suspense fallback={<div />}><ComparisonPage /></Suspense>} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/cookie-policy" element={<CookiePolicy />} />
    <Route path="/ai-policy" element={<AIUsagePolicy />} />
    <Route path="/data-retention" element={<DataRetentionPolicy />} />
    <Route path="/data-deletion" element={<DataDeletionInstructions />} />
    <Route path="/trust" element={<TrustCenter />} />
    <Route path="/waitlist" element={<WaitlistLanding />} />
    <Route path="/admin/waitlist" element={<P user={user} loading={loading}><WaitlistAdmin /></P>} />
    <Route path="/admin" element={<P user={user} loading={loading}><AdminDashboard /></P>} />
    <Route path="/profile" element={<P user={user} loading={loading}><UserProfile /></P>} />

    {/* Brand Setup */}
    <Route path="/brand-setup" element={<P user={user} loading={loading}><BrandSetup onComplete={onBrandSetupComplete} /></P>} />

    {/* Protected + BrandGate routes */}
    <Route path="/dashboard" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><Dashboard user={user} /></PB>} />
    <Route path="/documents" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><Documents /></PB>} />
    <Route path="/social" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><SocialListening /></PB>} />
    <Route path="/social-connections" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><SocialConnections /></PB>} />
    <Route path="/alerts" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><Alerts /></PB>} />
    <Route path="/social-studio" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><SocialStudio /></PB>} />
    <Route path="/smm" element={<PB user={user} loading={loading} brandSetupComplete={brandSetupComplete}><SMMOfficer /></PB>} />

    {/* Protected only routes */}
    <Route path="/history" element={<P user={user} loading={loading}><History /></P>} />
    <Route path="/stakeholders" element={<P user={user} loading={loading}><Stakeholders /></P>} />
    <Route path="/ai-tools" element={<P user={user} loading={loading}><AITools /></P>} />
    <Route path="/advanced" element={<P user={user} loading={loading}><Advanced /></P>} />
    <Route path="/team" element={<P user={user} loading={loading}><Team user={user} /></P>} />
    <Route path="/crisis-simulator" element={<P user={user} loading={loading}><CrisisSimulator /></P>} />
    <Route path="/media-impact" element={<P user={user} loading={loading}><MediaImpact /></P>} />
    <Route path="/virality-predictor" element={<P user={user} loading={loading}><ViralityPredictor /></P>} />
    <Route path="/auto-response" element={<P user={user} loading={loading}><AutoResponse /></P>} />
    <Route path="/stakeholder-behavior" element={<P user={user} loading={loading}><StakeholderBehavior /></P>} />
    <Route path="/brand-profile" element={<P user={user} loading={loading}><BrandProfileSettings /></P>} />
    <Route path="/analysis-history" element={<P user={user} loading={loading}><AnalysisHistory /></P>} />
    <Route path="/templates" element={<P user={user} loading={loading}><Templates /></P>} />
    <Route path="/startup-hub" element={<P user={user} loading={loading}><StartupHub /></P>} />
    <Route path="/social-media" element={<P user={user} loading={loading}><SocialMedia /></P>} />
    <Route path="/ai-analytics" element={<P user={user} loading={loading}><AIAnalytics /></P>} />
    <Route path="/meta-integration" element={<P user={user} loading={loading}><MetaIntegration /></P>} />
    <Route path="/api-integrations" element={<P user={user} loading={loading}><APIIntegrations /></P>} />
    <Route path="/social-connect" element={<P user={user} loading={loading}><SocialConnect user={user} /></P>} />
    <Route path="/media-directory" element={<P user={user} loading={loading}><MediaDirectory /></P>} />
    <Route path="/pr-toolkit" element={<P user={user} loading={loading}><PRToolkit /></P>} />
    <Route path="/sentiment" element={<P user={user} loading={loading}><SentimentDashboard /></P>} />
    <Route path="/intelligence" element={<P user={user} loading={loading}><IntelligenceDashboard /></P>} />
    <Route path="/risk" element={<P user={user} loading={loading}><PredictiveRiskDashboard /></P>} />
    <Route path="/briefs" element={<P user={user} loading={loading}><BriefGenerator /></P>} />
    <Route path="/stakeholder-map" element={<P user={user} loading={loading}><StakeholderMap /></P>} />
    <Route path="/forecast" element={<P user={user} loading={loading}><ReputationForecast /></P>} />
    <Route path="/political-intel" element={<P user={user} loading={loading}><PoliticalIntel /></P>} />

    <Route path="*" element={<Navigate to="/" replace />} />
  </>
);

export default AppRoutes;
